import { MULTI_APP_PARAM } from '../constants/multiApp';
import { tryMultiAppAutologin } from './tryMultiAppAutologin';
import { clearMultiAppFromLocation } from './clearMultiAppFromLocation';

const handoffByEncoded = {};
const handoffFinished = {};

const getEncodedMultiApp = search =>
  new URLSearchParams(search || '').get(MULTI_APP_PARAM) || '';

/**
 * Strips `?multiApp=` immediately so route guards never bounce to `/login?multiApp=`.
 */
export const stripMultiAppFromUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const next = clearMultiAppFromLocation({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash
  });
  const url = `${next.pathname}${next.search}${next.hash}`;
  window.history.replaceState({}, document.title, url);
};

const awaitInFlightHandoff = async () => {
  const pendingKeys = Object.keys(handoffByEncoded).filter(
    key => !handoffFinished[key]
  );
  if (!pendingKeys.length) {
    return false;
  }

  const { loggedIn } = await handoffByEncoded[pendingKeys[0]];
  return Boolean(loggedIn);
};

/**
 * StrictMode-safe multiApp receive handoff.
 * Captures search, strips the param immediately, exchanges once per encoded value.
 *
 * @param {{ search?: string, exchangeSession: Function, history?: object, location?: object }} params
 * @returns {Promise<boolean>}
 */
export const runMultiAppReceiveHandoff = async ({
  search,
  exchangeSession,
  history,
  location
} = {}) => {
  const searchWithMultiApp =
    search ||
    (typeof window !== 'undefined' ? window.location.search : '') ||
    '';
  let encodedMultiApp = getEncodedMultiApp(searchWithMultiApp);

  if (
    !encodedMultiApp &&
    typeof window !== 'undefined' &&
    searchWithMultiApp !== window.location.search
  ) {
    encodedMultiApp = getEncodedMultiApp(window.location.search);
  }

  // Remount after strip while exchange is still running.
  if (!encodedMultiApp) {
    return awaitInFlightHandoff();
  }

  if (handoffFinished[encodedMultiApp]) {
    return Boolean(handoffFinished[encodedMultiApp].loggedIn);
  }

  stripMultiAppFromUrl();

  if (!handoffByEncoded[encodedMultiApp]) {
    handoffByEncoded[encodedMultiApp] = tryMultiAppAutologin({
      search: searchWithMultiApp,
      exchangeSession,
      history,
      location
    })
      .then(loggedIn => ({ loggedIn }))
      .catch(() => {
        delete handoffByEncoded[encodedMultiApp];
        return { loggedIn: false };
      });
  }

  const { loggedIn } = await handoffByEncoded[encodedMultiApp];
  handoffFinished[encodedMultiApp] = { loggedIn };
  return Boolean(loggedIn);
};
